/**
 * useSEO Hook - Manages SEO meta tags including hreflang for 12 languages
 */
import { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { updateHreflangTags, updateMetaTags } from '../utils/seoHelpers';
import { supportedLanguageCodes, defaultLanguage } from '../i18n/config';

const SITE_URL = 'https://bolognacityexperience.com';
const DEFAULT_IMAGE = 'https://customer-assets.emergentagent.com/job_welcome-guests-6/artifacts/wo138l8s_foto%20panorama%20Bologna%20da%20S.Michele%20in%20Bosco.jpg';

// SEO data per language
const seoData = {
  it: { siteName: 'City Red Bus Bologna', title: 'Tour Panoramici e Esperienze Gastronomiche', desc: 'Scopri Bologna con City Red Bus: tour al tramonto con aperitivo, cooking class con tortellini.' },
  en: { siteName: 'City Red Bus Bologna', title: 'Panoramic Tours & Food Experiences', desc: 'Discover Bologna with City Red Bus: sunset tours with aperitivo, tortellini cooking classes.' },
  fr: { siteName: 'City Red Bus Bologne', title: 'Tours Panoramiques et Expériences Gastronomiques', desc: 'Découvrez Bologne avec City Red Bus.' },
  de: { siteName: 'City Red Bus Bologna', title: 'Panorama-Touren & Kulinarische Erlebnisse', desc: 'Entdecken Sie Bologna mit City Red Bus.' },
  es: { siteName: 'City Red Bus Bolonia', title: 'Tours Panorámicos y Experiencias Gastronómicas', desc: 'Descubre Bolonia con City Red Bus.' },
  pt: { siteName: 'City Red Bus Bolonha', title: 'Tours Panorâmicos e Experiências Gastronômicas', desc: 'Descubra Bolonha com City Red Bus.' },
  ru: { siteName: 'City Red Bus Болонья', title: 'Панорамные туры и гастрономические впечатления', desc: 'Откройте Болонью с City Red Bus.' },
  ja: { siteName: 'City Red Bus ボローニャ', title: 'パノラマツアー＆グルメ体験', desc: 'City Red Busでボローニャを発見。' },
  nl: { siteName: 'City Red Bus Bologna', title: 'Panoramische Tours & Culinaire Ervaringen', desc: 'Ontdek Bologna met City Red Bus.' },
  zh: { siteName: 'City Red Bus 博洛尼亚', title: '全景之旅和美食体验', desc: '与City Red Bus一起发现博洛尼亚。' },
  ko: { siteName: 'City Red Bus 볼로냐', title: '파노라마 투어 & 미식 체험', desc: 'City Red Bus와 함께 볼로냐를 발견하세요.' },
  pl: { siteName: 'City Red Bus Bolonia', title: 'Wycieczki Panoramiczne i Doświadczenia Kulinarne', desc: 'Odkryj Bolonię z City Red Bus.' }
};

const useSEO = (options = {}) => {
  const location = useLocation();
  const { lang } = useParams();
  
  const currentLang = supportedLanguageCodes.includes(lang) ? lang : defaultLanguage;
  const langData = seoData[currentLang] || seoData[defaultLanguage];
  
  useEffect(() => {
    // Update hreflang tags
    updateHreflangTags(location.pathname);
    
    // Build title
    const pageTitle = options.title 
      ? `${options.title} | ${langData.siteName}`
      : `${langData.siteName} | ${langData.title}`;
    
    // Build description
    const pageDesc = options.description || langData.desc;
    
    // Build URL
    const pageUrl = `${SITE_URL}${location.pathname}`;
    
    // Build locale
    const locale = currentLang === 'it' ? 'it_IT' : `${currentLang}_${currentLang.toUpperCase()}`;
    
    // Update meta tags
    updateMetaTags({
      title: pageTitle,
      description: pageDesc,
      image: options.image || DEFAULT_IMAGE,
      url: pageUrl,
      locale: locale
    });
  }, [location.pathname, currentLang, options.title, options.description, options.image, langData]);
};

export default useSEO;
